"use client";
import Button from "@/components/ui/Button";
import FloatingSelect from "@/components/ui/FloatingSelect";
import { useQuiz } from "@/hooks/useQuiz";
import {
  buildQuizSummary,
  getDomaineLabel,
  getSousDomaineLabel,
  isDomaineAvailable,
  type QuizSummaryItem,
} from "@/lib/quiz-config";
import { submitQuiz, type QuizSubmissionPayload } from "@/actions/quiz";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";

type Phase = "intro" | "questions";
type View = "intro" | "questions" | "result";

type Choice = { value: string; label: string };

// ---- Intro (pre-quiz) questions — options + labels kept together so the
// form rendering and the submission summary share a single source of truth.
const OBJECTIF_OPTIONS: Choice[] = [
  { value: "pro", label: "Progresser professionnellement" },
  { value: "hobby", label: "Apprendre pour le plaisir" },
  { value: "certif", label: "Obtenir une certification" },
  { value: "switch", label: "Changer de métier" },
  { value: "autre", label: "Autre..." },
];

const METIER_OPTIONS: Choice[] = [
  { value: "dev", label: "Développeur" },
  { value: "designer", label: "Designer UI/UX" },
  { value: "pm", label: "Product Manager" },
  { value: "autre", label: "Autre..." },
];

const NIVEAU_OPTIONS = [
  { id: "debutant", label: "Débutant", value: "debutant" },
  { id: "intermediaire", label: "Intermédiaire", value: "intermediaire" },
  { id: "avance", label: "Avancé", value: "avance" },
];

const INTRO_QUESTIONS = {
  objectif: "Quel est votre objectif principal d'apprentissage ?",
  metier: "Quel est votre métier ?",
  niveau:
    "Quel est votre niveau d'expérience général dans le domaine qui vous intéresse ?",
  motivation: "Qu'est-ce qui motive votre demande de formation ?",
} as const;

const labelFor = (options: Choice[], value: string): string =>
  options.find((o) => o.value === value)?.label ?? value;

export default function QuizContent() {
  const [phase, setPhase] = useState<Phase>("intro");
  const [profile, setProfile] = useState({
    nom: "",
    prenom: "",
    email: "",
    phone: "",
    objectif: "",
    metier: "",
    niveau: "",
    motivation: "",
  });
  const quiz = useQuiz();

  const router = useRouter();

  // Once started, the flow moves to "result" on its own when every question is answered.
  const view: View =
    phase === "intro" ? "intro" : quiz.isComplete ? "result" : "questions";
  const isResult = view === "result";

  // ---- Submission — fire once the quiz completes -----------------------
  const submittedRef = useRef(false);
  const [submitError, setSubmitError] = useState(false);

  const sendSubmission = useCallback(() => {
    const intro: QuizSummaryItem[] = [];
    if (profile.objectif)
      intro.push({ key: "objectif", question: INTRO_QUESTIONS.objectif, answer: labelFor(OBJECTIF_OPTIONS, profile.objectif) });
    if (profile.metier)
      intro.push({ key: "metier", question: INTRO_QUESTIONS.metier, answer: labelFor(METIER_OPTIONS, profile.metier) });
    if (profile.niveau)
      intro.push({ key: "niveau", question: INTRO_QUESTIONS.niveau, answer: labelFor(NIVEAU_OPTIONS, profile.niveau) });
    if (profile.motivation)
      intro.push({ key: "motivation_intro", question: INTRO_QUESTIONS.motivation, answer: profile.motivation });

    const payload: QuizSubmissionPayload = {
      first_name: profile.prenom,
      last_name: profile.nom,
      email: profile.email,
      phone: profile.phone,
      source: "Quiz d'orientation",
      formation_theme: quiz.answers.domaine || undefined,
      answers: [...intro, ...buildQuizSummary(quiz.answers)],
    };

    submitQuiz(payload).catch(() => setSubmitError(true));
  }, [profile, quiz.answers]);

  useEffect(() => {
    if (!quiz.isComplete || submittedRef.current) return;
    submittedRef.current = true;
    sendSubmission();
  }, [quiz.isComplete, sendSubmission]);

  const retrySubmission = useCallback(() => {
    setSubmitError(false);
    sendSubmission();
  }, [sendSubmission]);

  const canStart =
    profile.nom &&
    profile.prenom &&
    profile.email &&
    profile.phone &&
    profile.objectif &&
    profile.metier &&
    profile.niveau &&
    profile.motivation;

  // Progress (left panel) — driven by the engine so branching stays in sync.
  const progressIndex = quiz.step - 1;
  const total = quiz.total;
  const estimatedMinutes = Math.max(1, Math.ceil((total * 30) / 60));
  const remainingMinutes = Math.max(
    1,
    Math.ceil(((total - progressIndex) * 30) / 60),
  );

  // Result (right/left panels) — reflects the chosen domain; non-marketing
  // domains stop after Q2 ("Arrêt si formation recherchée autre que marketing").
  const currentQuestion = quiz.current;
  const freeText = currentQuestion?.question.freeText;
  const domaineLabel = getDomaineLabel(quiz.answers.domaine);
  const sousDomaineLabel = getSousDomaineLabel(
    quiz.answers.domaine,
    quiz.answers.sous_domaine,
  );
  const domaineAvailable = isDomaineAvailable(quiz.answers.domaine);

  return (
    <div
      className={`min-h-[calc(100vh-66px)] ${isResult ? "bg-[#C7F8FF]" : "bg-white"}`}
    >
      <div className="bg-[#1B365E] h-16.5 w-full overflow-visible relative ">
        {/* Header pill logo */}
        <div className="absolute z-2 left-1/2 -translate-x-1/2 top-12 flex h-10.25 w-15.5 items-center justify-center rounded-full border-b border-[#40BFDE] bg-white">
          <Image
            className="w-7.5 h-4.25"
            src="/assets/small-logo.svg"
            alt="logo"
            width={30}
            height={17}
          />
        </div>
      </div>
      <div
        className={`${isResult ? "bg-[#C7F8FF]" : "bg-white"} relative overflow-hidden`}
      >
        {/* Two columns */}
        <div
          className={`grid grid-cols-1 ${isResult ? "md:grid-cols-1" : "lg:grid-cols-[555px_1fr] md:grid-cols-[385px_1fr]"} min-h-[calc(100vh-66px)]`}
        >
          {/* Left panel */}
          <aside
            className={`${isResult ? "hidden" : "block"} bg-[#C7F8FF] px-4 md:px-5 md:py-19.25 py-10`}
          >
            {view === "intro" && (
              <div className="text-center">
                {/* "Bienvenue Mr/Ms" + the "Quiz d'orientation et de niveau"
                    strapline were dropped at the client's request. */}
                <div className="md:my-10 my-6 flex items-center justify-center">
                  <Image
                    src="/assets/quiz-img.png"
                    alt="illustration"
                    width={198}
                    height={178}
                    className="rounded-md"
                  />
                </div>
                <p className="text-xs text-[#000000CC]">
                  Le temps estimé est de {estimatedMinutes}{" "}
                  {estimatedMinutes > 1 ? "minutes" : "minute"}
                </p>
                <p className="text-xs text-[#000000CC] md:mt-8 mt-5">
                  Répondez à quelques questions pour identifier vos besoins et
                  votre niveau, et accéder aux formations les plus pertinentes
                  pour votre parcours.
                </p>
              </div>
            )}

            {view === "questions" && (
              <div className="md:space-y-10 space-y-6 text-center">
                <p className="md:pt-4 text-xs text-[#000000CC]">Étape :</p>
                <p className="text-[40px] font-red-hat text-[#1B365E] font-light">
                  {Math.min(progressIndex + 1, total)}/{total}
                </p>
                <p className="text-xs text-[#000000CC]">
                  Le temps restant estimé est de {remainingMinutes}{" "}
                  {remainingMinutes > 1 ? "minutes" : "minute"}
                </p>
                <div className="mt-6 flex gap-2 justify-center">
                  {Array.from({ length: total }).map((_, i) => {
                    const isActive = i === progressIndex;
                    const isPrevious = i < progressIndex;
                    return (
                      <span
                        key={i}
                        className={`h-2 w-2 rounded-full ${
                          isActive
                            ? "bg-[#1B365E]"
                            : isPrevious
                              ? "bg-[#40BFDE]"
                              : "bg-[#D9D9D9]"
                        }`}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {view === "result" && (
              <div className="md:space-y-8 space-y-6">
                <h3 className="text-[40px] font-red-hat font-light text-[#1B365E]">
                  Très bien
                </h3>
                <p className="text-[#1B365E] text-[20px] font-medium">
                  {domaineAvailable
                    ? "Nous vous recommandons :"
                    : "Votre demande est enregistrée"}
                </p>
                <p className="text-[30px] font-red-hat font-light text-[#1B365E]">
                  {domaineLabel}
                </p>
                <span className="inline-block text-[8px] px-2 py-1 rounded-full bg-white text-[#1B515E]">
                  {sousDomaineLabel || domaineLabel}
                </span>
              </div>
            )}
          </aside>

          {/* Right content */}
          <section
            className={`${isResult ? "md:pt-19.25 pt-11 md:px-7 px-4" : "pt-11 md:pt-43.5 px-6 lg:px-14  md:px-10"} pb-10 md:pb-16 relative`}
          >
            <button
              className="absolute right-4 md:right-11.25 top-4 md:top-9.25 md:w-6 w-5 md:h-6 h-5"
              aria-label="Fermer"
              onClick={() => router.back()}
            >
              <Image
                src="/assets/icons/icon-close.svg"
                alt="illustration"
                width={24}
                height={24}
                className="rounded-md"
              />
            </button>

            {view === "intro" && (
              <div className="max-w-360">
                <p className="text-[#1B365E]">
                Pour mieux vous orienter, nous aurons besoin de quelques
                informations avant de commencer le Quiz
                </p>

                <div className="mt-8 md:space-y-10 space-y-6">
                <label className="block text-base text-[#1B365E]">
                    Veuillez remplir vos informations personnelles ?
                </label>
                {/* Nom / Prénom */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-103 md:max-w-none">
                    <div className="space-y-2">
                    <label className="block text-base text-[#1B365E]">Nom</label>
                    <input
                        type="text"
                        name="nom"
                        className="w-full rounded-lg border border-[#EFEFEF] px-3 py-2.5 text-[#1D1D1D] text-xs placeholder:text-[#ABABAB] focus:outline-none focus:ring-0 focus:border-[#40BFDE]"
                        placeholder="Votre nom"
                        value={profile.nom}
                        onChange={(e) =>
                        setProfile((p) => ({ ...p, nom: e.target.value }))
                        }
                    />
                    </div>

                    <div className="space-y-2">
                    <label className="block text-base text-[#1B365E]">Prénom</label>
                    <input
                        type="text"
                        name="prenom"
                        className="w-full rounded-lg border border-[#EFEFEF] px-3 py-2.5 text-[#1D1D1D] text-xs placeholder:text-[#ABABAB] focus:outline-none focus:ring-0 focus:border-[#40BFDE]"
                        placeholder="Votre prénom"
                        value={profile.prenom}
                        onChange={(e) =>
                        setProfile((p) => ({ ...p, prenom: e.target.value }))
                        }
                    />
                    </div>
                </div>

                {/* Email */}
                <div className="max-w-103 space-y-2">
                    <label className="block text-base text-[#1B365E]">Email</label>
                    <input
                    type="email"
                    name="email"
                    className="w-full rounded-lg border border-[#EFEFEF] px-3 py-2.5 text-[#1D1D1D] text-xs placeholder:text-[#ABABAB] focus:outline-none focus:ring-0 focus:border-[#40BFDE]"
                    placeholder="Votre email"
                    value={profile.email}
                    onChange={(e) =>
                        setProfile((p) => ({ ...p, email: e.target.value }))
                    }
                    />
                </div>

                {/* Téléphone */}
                <div className="max-w-103 space-y-2">
                    <label className="block text-base text-[#1B365E]">Téléphone</label>
                    <input
                    type="tel"
                    name="phone"
                    className="w-full rounded-lg border border-[#EFEFEF] px-3 py-2.5 text-[#1D1D1D] text-xs placeholder:text-[#ABABAB] focus:outline-none focus:ring-0 focus:border-[#40BFDE]"
                    placeholder="Votre numéro de téléphone"
                    value={profile.phone}
                    onChange={(e) =>
                        setProfile((p) => ({ ...p, phone: e.target.value }))
                    }
                    />
                </div>

                <div className="max-w-103">
                    <FloatingSelect
                    label={INTRO_QUESTIONS.objectif}
                    name="objectif"
                    value={profile.objectif}
                    onChange={(e) =>
                        setProfile((p) => ({ ...p, objectif: e.target.value }))
                    }
                    placeholder="Objectif principal"
                    options={OBJECTIF_OPTIONS}
                    />
                </div>

                <div className="max-w-103">
                    <FloatingSelect
                    label={INTRO_QUESTIONS.metier}
                    name="metier"
                    value={profile.metier}
                    onChange={(e) =>
                        setProfile((p) => ({ ...p, metier: e.target.value }))
                    }
                    placeholder="Votre métier"
                    options={METIER_OPTIONS}
                    />
                </div>

                <div className="md:space-y-7 space-y-5">
                    <label className="block text-base text-[#1B365E]">
                    {INTRO_QUESTIONS.niveau}
                    </label>
                    <div className="md:space-y-7 space-y-5">
                    {NIVEAU_OPTIONS.map((option) => (
                        <label
                        key={option.id}
                        className="flex items-center gap-2 cursor-pointer group"
                        >
                        <input
                            type="radio"
                            id={option.id}
                            name="experience"
                            value={option.value}
                            className="sr-only"
                            checked={profile.niveau === option.value}
                            onChange={() =>
                            setProfile((p) => ({
                                ...p,
                                niveau: option.value,
                            }))
                            }
                        />

                        <div
                            className={`
                            w-6 h-6 rounded-full border shrink-0
                            flex items-center justify-center
                            transition-colors
                            ${
                            profile.niveau === option.value
                                ? "border-[#40BFDE] bg-white"
                                : "border-gray-300 group-hover:border-gray-400 bg-white"
                            }
                        `}
                        >
                            <div
                            className={`
                            w-4 h-4 rounded-full
                            ${profile.niveau === option.value ? "bg-[#40BFDE]" : "bg-transparent"}
                            transition-all duration-200
                            `}
                            />
                        </div>

                        <span className="text-[14px] text-[#1D1D1D]">
                            {option.label}
                        </span>
                        </label>
                    ))}
                    </div>
                </div>

                <div className="md:space-y-7 space-y-5">
                    <label className="block text-base text-[#1B365E]">
                    {INTRO_QUESTIONS.motivation}
                    </label>
                    <input
                    className="w-full rounded-lg border border-[#EFEFEF] px-3 py-2.5 text-[#1D1D1D] text-xs placeholder:text-[#ABABAB] focus:outline-none focus:ring-0 focus:border-[#40BFDE]"
                    placeholder="Votre motivation en quelques mots"
                    value={profile.motivation}
                    onChange={(e) =>
                        setProfile((p) => ({
                        ...p,
                        motivation: e.target.value,
                        }))
                    }
                    />
                </div>

                <button
                    disabled={!canStart}
                    onClick={() => setPhase("questions")}
                    className={`cursor-pointer rounded-lg px-6 py-3 border border-[#ABABAB] text-white transition ${canStart ? "bg-[#1B365E] hover:opacity-90" : "text-[#ABABAB] bg-[#D9D9D9] cursor-not-allowed"}`}
                >
                    Commencer le Quiz
                </button>
                </div>
              </div>
            )}

            {view === "questions" && currentQuestion && (
              <div className="max-w-xl">
                <p className="text-[#1B365E] mb-7">
                  Répondez à chaque question pour affiner votre orientation.
                </p>
                <div className="md:space-y-7 space-y-5">
                  <h3 className="text-[#1B365E]">
                    {quiz.step}. {currentQuestion.title}
                  </h3>
                  {currentQuestion.question.objective && (
                    <p className="text-xs text-[#00000099]">
                      {currentQuestion.question.objective}
                    </p>
                  )}
                  <div className="md:space-y-7 space-y-5">
                    {currentQuestion.options.map((op) => {
                      const selected = quiz.draft === op.value;
                      const inputId = `q-${currentQuestion.question.id}-${op.value}`;
                      return (
                        <label
                          key={op.value}
                          htmlFor={inputId}
                          className={`cursor-pointer w-full rounded-full border p-2.5 text-left transition flex items-center gap-2 ${
                            selected
                              ? "border-[#40BFDE] shadow-[inset_0_0_0_2px_#1B365E]"
                              : "border-[#40BFDE] hover:border-[#1B365E]"
                          }`}
                        >
                          <input
                            type="radio"
                            id={inputId}
                            name={currentQuestion.question.id}
                            value={op.value}
                            className="sr-only"
                            checked={selected}
                            onChange={() => quiz.select(op.value)}
                          />

                          <div
                            className={`
                                  w-6 h-6 rounded-full border shrink-0
                                  flex items-center justify-center
                                  transition-colors
                                  ${
                                    selected
                                      ? "border-[#40BFDE] bg-white"
                                      : "border-gray-300 bg-white"
                                  }
                                `}
                          >
                            <div
                              className={`
                                  w-4 h-4 rounded-full
                                  ${selected ? "bg-[#40BFDE]" : "bg-transparent"}
                                  transition-all duration-200
                                `}
                            />
                          </div>

                          <span className="text-[#1B365E]">{op.label}</span>
                        </label>
                      );
                    })}
                  </div>

                  {freeText && (
                    <input
                      className="w-full rounded-lg border border-[#EFEFEF] px-3 py-2.5 text-[#1D1D1D] text-xs placeholder:text-[#ABABAB] focus:outline-none focus:ring-0 focus:border-[#40BFDE]"
                      placeholder={freeText.placeholder}
                      value={quiz.answers[freeText.id] ?? ""}
                      onChange={(e) =>
                        quiz.setFreeText(freeText.id, e.target.value)
                      }
                    />
                  )}

                  <button
                    onClick={quiz.validate}
                    disabled={!quiz.draft}
                    className={`cursor-pointer rounded-lg px-10 py-4 border transition ${quiz.draft ? "text-white bg-[#1B365E] hover:opacity-90" : "border-[#ABABAB] text-[#ABABAB] bg-[#D9D9D9] cursor-not-allowed"}`}
                  >
                    Valider ma réponse
                  </button>
                </div>
              </div>
            )}

            {view === "result" && (
              <div className="text-center">
                <div className="w-full h-47 rounded-xl overflow-hidden border border-[#FFE4B4] mb-8">
                  <Image
                    src="/assets/quiz-result-img.png"
                    alt="banner"
                    width={1200}
                    height={200}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="max-w-126.75 mx-auto space-y-8">
                  {submitError && (
                    <div className="rounded-lg border border-[#FF5758] bg-[#FFF1F1] px-4 py-3 text-left">
                      <p className="text-[14px] text-[#FF5758]">
                        Un problème est survenu lors de l&apos;envoi de vos réponses.
                      </p>
                      <button
                        onClick={retrySubmission}
                        className="mt-2 cursor-pointer text-[14px] font-bold text-[#1B365E] underline"
                      >
                        Réessayer
                      </button>
                    </div>
                  )}
                  <h3 className="text-[40px] font-red-hat tracking-[3%] font-light text-[#1B365E] mb-2">
                    Très bien
                  </h3>
                  <p className="text-[#1B365E] font-medium text-[20px]">
                    {domaineAvailable
                      ? "Nous vous recommandons :"
                      : "Votre demande est enregistrée"}
                  </p>
                  <p className="text-[30px] font-red-hat font-light tracking-[3%] text-[#1B365E] mb-4">
                    {domaineLabel}
                  </p>
                  {sousDomaineLabel && (
                    <span className="inline-block text-[10px] px-2 py-1 leading-5 rounded-full bg-white text-[#1B365E]">
                      {sousDomaineLabel}
                    </span>
                  )}
                  <p className="max-w-87.25 mx-auto text-[#000000CC] text-xs">
                    {domaineAvailable
                      ? "Découvrez les formations les plus pertinentes pour votre profil et vos objectifs, puis inscrivez-vous en quelques clics."
                      : `Les formations en ${domaineLabel} arrivent bientôt : nous revenons vers vous dès qu'elles sont disponibles.`}
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <Link
                      href="/formations"
                      className="p-4 cursor-pointer text-[#1B365E] text-[15px] font-bold flex items-center gap-2"
                    >
                      <svg
                        className="min-w-4"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8ZM4.75 7.25C4.33579 7.25 4 7.58579 4 8C4 8.41421 4.33579 8.75 4.75 8.75L9.43934 8.75L8.21967 9.96967C7.92678 10.2626 7.92678 10.7374 8.21967 11.0303C8.51256 11.3232 8.98744 11.3232 9.28033 11.0303L11.7803 8.53033C12.0732 8.23744 12.0732 7.76256 11.7803 7.46967L9.28033 4.96967C8.98744 4.67678 8.51256 4.67678 8.21967 4.96967C7.92678 5.26256 7.92678 5.73744 8.21967 6.03033L9.43934 7.25L4.75 7.25Z"
                          fill="#1B365E"
                        />
                      </svg>
                      Découvrir
                    </Link>

                    <Button
                      onClick={() => router.push("/login")}
                      variant="gradient"
                      size="unset"
                      className="h-14.5 w-37 rounded-lg text-[15px] py-4 font-bold"
                    >
                      S’inscrire
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
